import React, { useEffect, useMemo, useState } from 'react';
// Commissions.tsx - VERSION DEFINITIVA
import { PercentIcon, SaveIcon, FilterIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Tabs } from '../ui/Tabs';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Pagination } from '../ui/Pagination';
import { usePagination } from '../../hooks/usePagination';
import { supabase } from '../../utils/supabase';
import { formatCurrency } from '../../utils/helpers';
export function Commissions() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [sales, setSales] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [servicePercents, setServicePercents] = useState<Record<string, string>>({});
  const [productPercents, setProductPercents] = useState<Record<string, string>>({});
  const [saveMsg, setSaveMsg] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(today);
  const [selectedUser, setSelectedUser] = useState('all');
  useEffect(() => {
    loadAll();
  }, []);
  // Construir lista de personas para comisiones, deduplicada por nombre
  function buildPeopleList(usersData: any[], doctorsData: any[]) {
    const allowedRoles = new Set(['doctor', 'doctora', 'terapeuta', 'terapista', 'recepcion', 'recepción', 'recepcionista', 'callcenter', 'call center', 'call_center']);
    const people: any[] = [];
    const seenNames = new Set<string>();
    // 1. Primero agregar usuarios con roles permitidos (prioridad)
    usersData.forEach((u: any) => {
      if (!allowedRoles.has((u.role || '').toLowerCase())) return;
      const key = (u.name || '').trim().toLowerCase();
      if (key && !seenNames.has(key)) {
        seenNames.add(key);
        people.push({
          id: u.id,
          name: u.name
        });
      }
    });
    // 2. Luego agregar doctores/terapeutas que no estén ya por nombre
    doctorsData.forEach((d: any) => {
      const key = (d.name || '').trim().toLowerCase();
      if (key && !seenNames.has(key)) {
        seenNames.add(key);
        people.push({
          id: d.id,
          name: d.name
        });
      }
    });
    return people;
  }
  async function loadAll() {
    setLoading(true);
    try {
      const [configRes, patientsRes, doctorsRes, usersRes, servicesRes, appointmentsRes, salesRes, productsRes] = await Promise.all([supabase.from('commission_configs').select('*'), supabase.from('patients').select('*'), supabase.from('doctors').select('*'), supabase.from('users').select('id, name, role'), supabase.from('services').select('*'), supabase.from('appointments').select('*, patients:patient_id(id, name), doctors_rel:doctor_id(id, name), services_rel:service_id(id, name, price)'), supabase.from('product_sales').select('*'), supabase.from('products').select('id, name')]);
      const configs = configRes.data || [];
      const patientsData = patientsRes.data || [];
      const doctorsData = doctorsRes.data || [];
      const usersData = usersRes.data || [];
      const servicesData = servicesRes.data || [];
      const appointmentsData = appointmentsRes.data || [];
      const salesData = salesRes.data || [];
      const productsData = productsRes.data || [];
      // Debug: mostrar configs cargados
      console.log('📊 Configs cargados:', configs.length);
      configs.forEach((c: any) => console.log(`  → ${c.person_name} | scope=${c.commission_scope} | percent=${c.commission_percent} | pid=${c.person_id}`));
      // Construir lista de personas deduplicada por nombre
      const allPeople = buildPeopleList(usersData, doctorsData);
      console.log('📊 Personas construidas:', allPeople.map((p: any) => `${p.name} (${p.id})`).join(', '));
      // Leer porcentajes de configs existentes (mapear por nombre Y por ID para cruzar)
      const sp: Record<string, string> = {};
      const pp: Record<string, string> = {};
      // Crear mapas de nombre → porcentajes Y id → porcentajes desde configs
      const configByName: Record<string, {
        service: string;
        product: string;
      }> = {};
      const configById: Record<string, {
        service: string;
        product: string;
      }> = {};
      configs.forEach((row: any) => {
        const name = (row.person_name || '').trim().toLowerCase();
        const pid = String(row.person_id || '');
        const scope = row.commission_scope || 'service';
        const val = String(row.commission_percent || 0);
        // Map by name
        if (name) {
          if (!configByName[name]) configByName[name] = {
            service: '0',
            product: '0'
          };
          if (scope === 'service') configByName[name].service = val;
          if (scope === 'product') configByName[name].product = val;
        }
        // Map by person_id
        if (pid) {
          if (!configById[pid]) configById[pid] = {
            service: '0',
            product: '0'
          };
          if (scope === 'service') configById[pid].service = val;
          if (scope === 'product') configById[pid].product = val;
        }
      });
      // Asignar porcentajes a cada persona por ID primero, luego por nombre como fallback
      allPeople.forEach((p: any) => {
        const byId = configById[String(p.id)];
        const byName = configByName[(p.name || '').trim().toLowerCase()];
        const found = byId || byName;
        sp[p.id] = found?.service || '0';
        pp[p.id] = found?.product || '0';
        if (found) {
          console.log(`📊 ${p.name}: service=${found.service}, product=${found.product} (matched by ${byId ? 'ID' : 'name'})`);
        } else {
          console.log(`⚠️ ${p.name}: no config found (id=${p.id})`);
        }
      });
      setUsers(allPeople);
      setServicePercents(sp);
      setProductPercents(pp);
      setPatients(patientsData);
      setDoctors(doctorsData);
      setServices(servicesData);
      setAppointments(appointmentsData);
      setSales(salesData);
      setProducts(productsData);
    } catch (err) {
      console.error('Error loading commissions:', err);
    } finally {
      setLoading(false);
    }
  }
  async function saveAll() {
    try {
      // 1. Borrar TODA la tabla commission_configs
      await supabase.from('commission_configs').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      // 2. Insertar exactamente 2 filas por persona (service + product)
      const rows: any[] = [];
      users.forEach((u: any) => {
        rows.push({
          person_id: u.id,
          person_name: u.name,
          person_type: 'usuario',
          commission_scope: 'service',
          commission_percent: Number(servicePercents[u.id] || 0),
          is_active: true
        });
        rows.push({
          person_id: u.id,
          person_name: u.name,
          person_type: 'usuario',
          commission_scope: 'product',
          commission_percent: Number(productPercents[u.id] || 0),
          is_active: true
        });
      });
      if (rows.length > 0) {
        const {
          error: insertError
        } = await supabase.from('commission_configs').insert(rows);
        if (insertError) {
          console.error('❌ Error insertando comisiones:', insertError);
          setSaveMsg(`❌ Error al insertar: ${insertError.message}`);
          return;
        }
      }
      setSaveMsg('✅ Guardado correctamente ✓');
    } catch (err: any) {
      console.error('Error guardando comisiones:', err);
      setSaveMsg(`❌ Error al guardar: ${err.message || 'Error desconocido'}`);
    }
    setTimeout(() => {
      setSaveMsg('');
    }, 3000);
    await loadAll();
  }
  /* =====================
     CITAS
  ===================== */
  const appointmentRows = useMemo(() => {
    return appointments.filter((x: any) => x.status === 'completed' || x.status === 'completada').map((apt: any) => {
      const userId = apt.created_by_user_id || apt.user_id;
      // Use JOIN data first, then fallback to separate lookups
      const patient = apt.patients?.name || patients.find((p: any) => String(p.id) === String(apt.patient_id))?.name || apt.patient_name || apt.patientName || 'Paciente';
      const doctor = apt.doctors_rel?.name || doctors.find((d: any) => String(d.id) === String(apt.doctor_id))?.name || apt.doctor_name || apt.doctorName || 'Doctor';
      const joinedService = apt.services_rel;
      const service = joinedService || services.find((s: any) => String(s.id) === String(apt.service_id));
      const serviceName = service?.name || apt.service_name || apt.serviceName || 'Servicio';
      const total = Number(service?.price || apt.total || apt.amount || 0);
      const percent = Number(servicePercents[userId] || 0);
      const userName = users.find((u: any) => String(u.id) === String(userId))?.name || apt.created_by_user_name || apt.createdByUserName || 'Usuario';
      const date = (apt.date || apt.created_at || '').slice(0, 10);
      return {
        id: apt.id,
        date,
        patient,
        doctor,
        service: serviceName,
        total,
        commission: total * (percent / 100),
        userName,
        userId
      };
    }).filter((x: any) => {
      if (x.date < dateFrom) return false;
      if (x.date > dateTo) return false;
      if (selectedUser !== 'all' && String(x.userId) !== String(selectedUser)) return false;
      return true;
    });
  }, [appointments, patients, doctors, services, users, servicePercents, dateFrom, dateTo, selectedUser]);
  /* =====================
     PRODUCTOS
  ===================== */
  const productRows = useMemo(() => {
    // Build a product name lookup map using String keys for safe comparison
    const productNameMap: Record<string, string> = {};
    products.forEach((p: any) => {
      productNameMap[String(p.id)] = p.name;
    });
    return sales.flatMap((sale: any) => {
      const userId = sale.created_by_user_id || sale.user_id;
      const percent = Number(productPercents[userId] || 0);
      const userName = users.find((u: any) => String(u.id) === String(userId))?.name || sale.created_by_user_name || 'Usuario';
      const date = (sale.date || sale.created_at || '').slice(0, 10);
      const items = Array.isArray(sale.items) ? sale.items : [];
      if (items.length === 0) {
        // Fallback: show as single row with total
        const total = Number(sale.total || 0);
        return [{
          id: sale.id,
          date,
          product: 'Venta de productos',
          total,
          commission: total * (percent / 100),
          userName,
          userId
        }];
      }
      // Expand each item in the sale into its own commission row
      return items.map((item: any, idx: number) => {
        const productName = productNameMap[String(item.productId)] || item.productName || 'Producto';
        const subtotal = Number(item.subtotal || (item.unitPrice || 0) * (item.quantity || 0));
        return {
          id: `${sale.id}-${idx}`,
          date,
          product: `${productName} (x${item.quantity || 1})`,
          total: subtotal,
          commission: subtotal * (percent / 100),
          userName,
          userId
        };
      });
    }).filter((x: any) => {
      if (x.date < dateFrom) return false;
      if (x.date > dateTo) return false;
      if (selectedUser !== 'all' && String(x.userId) !== String(selectedUser)) return false;
      return true;
    });
  }, [sales, products, users, productPercents, dateFrom, dateTo, selectedUser]);
  const totalServices = appointmentRows.reduce((a, b) => a + b.commission, 0);
  const totalProducts = productRows.reduce((a, b) => a + b.commission, 0);
  const totalGeneral = totalServices + totalProducts;
  const configPagination = usePagination(users, {
    itemsPerPage: 8
  });
  const aptPagination = usePagination(appointmentRows, {
    itemsPerPage: 10
  });
  const productPagination = usePagination(productRows, {
    itemsPerPage: 10
  });
  if (loading) return <div className="p-10 text-center" data-id="element-1193">Cargando...</div>;
  return <div className="space-y-6" data-id="element-1194">
      {/* HEADER */}

      <div className="flex justify-between items-center" data-id="element-1195">
        <h1 className="text-3xl font-bold flex gap-2 items-center" data-id="element-1196">
          <PercentIcon className="w-8 h-8 text-indigo-600" data-id="element-1197" />
          Comisiones
        </h1>

        <Button onClick={saveAll} data-id="element-1198">
          <SaveIcon className="w-4 h-4 mr-2" data-id="element-1199" />
          Guardar
        </Button>
      </div>

      {saveMsg && <div className="text-green-600 font-medium" data-id="element-1200">{saveMsg}</div>}

      {/* CUADROS */}

      <div className="grid md:grid-cols-3 gap-5" data-id="element-1201">
        <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-0 shadow-xl" data-id="element-1202">
          <CardContent className="p-6" data-id="element-1203">
            <p data-id="element-1204">Comisión Servicios</p>
            <h2 className="text-4xl font-black mt-2" data-id="element-1205">
              {formatCurrency(totalServices)}
            </h2>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0 shadow-xl" data-id="element-1206">
          <CardContent className="p-6" data-id="element-1207">
            <p data-id="element-1208">Comisión Productos</p>
            <h2 className="text-4xl font-black mt-2" data-id="element-1209">
              {formatCurrency(totalProducts)}
            </h2>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white border-0 shadow-xl" data-id="element-1210">
          <CardContent className="p-6" data-id="element-1211">
            <p data-id="element-1212">Total General</p>
            <h2 className="text-4xl font-black mt-2" data-id="element-1213">
              {formatCurrency(totalGeneral)}
            </h2>
          </CardContent>
        </Card>
      </div>

      {/* FILTROS */}

      <Card data-id="element-1214">
        <CardHeader data-id="element-1215">
          <CardTitle data-id="element-1216">
            <FilterIcon className="w-4 h-4 inline mr-2" data-id="element-1217" />
            Filtros
          </CardTitle>
        </CardHeader>

        <CardContent data-id="element-1218">
          <div className="grid md:grid-cols-3 gap-4" data-id="element-1219">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border rounded-xl px-3 py-2" data-id="element-1220" />

            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border rounded-xl px-3 py-2" data-id="element-1221" />

            <Select value={selectedUser} onChange={setSelectedUser} options={[{
            value: 'all',
            label: 'Todos'
          }, ...users.map((u: any) => ({
            value: u.id,
            label: u.name
          }))]} data-id="element-1222" />
          </div>
        </CardContent>
      </Card>

      {/* CONFIG */}

      <Card data-id="element-1223">
        <CardHeader data-id="element-1224">
          <CardTitle data-id="element-1225">Configuración</CardTitle>
        </CardHeader>

        <CardContent data-id="element-1226">
          <table className="w-full" data-id="element-1227">
            <thead data-id="element-1228">
              <tr className="bg-gray-50 border-b" data-id="element-1229">
                <th className="text-left px-4 py-3" data-id="element-1230">Usuario</th>
                <th className="text-center" data-id="element-1231">Servicios %</th>
                <th className="text-center" data-id="element-1232">Productos %</th>
              </tr>
            </thead>

            <tbody data-id="element-1233">
              {configPagination.paginatedItems.map((u: any) => <tr key={u.id} className="border-b" data-id="element-1234">
                  <td className="px-4 py-3" data-id="element-1235">{u.name}</td>

                  <td className="text-center" data-id="element-1236">
                    <input value={servicePercents[u.id] || ''} onChange={e => setServicePercents(prev => ({
                  ...prev,
                  [u.id]: e.target.value
                }))} className="border rounded-xl w-24 text-center px-2 py-1" data-id="element-1237" />
                  </td>

                  <td className="text-center" data-id="element-1238">
                    <input value={productPercents[u.id] || ''} onChange={e => setProductPercents(prev => ({
                  ...prev,
                  [u.id]: e.target.value
                }))} className="border rounded-xl w-24 text-center px-2 py-1" data-id="element-1239" />
                  </td>
                </tr>)}
            </tbody>
          </table>

          <Pagination {...configPagination} data-id="element-1240" />
        </CardContent>
      </Card>

      {/* TABLAS */}

      <Tabs tabs={[{
      id: 'citas',
      label: 'Comisión por Citas',
      content: <Card data-id="element-1242">
                <CardContent className="p-0 overflow-x-auto" data-id="element-1243">
                  <table className="w-full min-w-[950px]" data-id="element-1244">
                    <thead className="bg-gray-50 border-b" data-id="element-1245">
                      <tr data-id="element-1246">
                        <th className="px-4 py-3 text-left" data-id="element-1247">Fecha</th>
                        <th className="px-4 py-3 text-left" data-id="element-1248">Paciente</th>
                        <th className="px-4 py-3 text-left" data-id="element-1249">Doctor</th>
                        <th className="px-4 py-3 text-left" data-id="element-1250">Servicio</th>
                        <th className="px-4 py-3 text-right" data-id="element-1251">Total</th>
                        <th className="px-4 py-3 text-right" data-id="element-1252">Comisión</th>
                        <th className="px-4 py-3 text-center" data-id="element-1253">Usuario</th>
                      </tr>
                    </thead>

                    <tbody data-id="element-1254">
                      {aptPagination.paginatedItems.map((row: any) => <tr key={row.id} className="border-b" data-id="element-1255">
                          <td className="px-4 py-3" data-id="element-1256">{row.date}</td>
                          <td className="px-4 py-3" data-id="element-1257">{row.patient}</td>
                          <td className="px-4 py-3" data-id="element-1258">{row.doctor}</td>
                          <td className="px-4 py-3" data-id="element-1259">{row.service}</td>
                          <td className="px-4 py-3 text-right" data-id="element-1260">
                            {formatCurrency(row.total)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-600 font-bold" data-id="element-1261">
                            {formatCurrency(row.commission)}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-1262">
                            <Badge data-id="element-1263">{row.userName}</Badge>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>

                  <div className="p-4" data-id="element-1264">
                    <Pagination {...aptPagination} data-id="element-1265" />
                  </div>
                </CardContent>
              </Card>
    }, {
      id: 'productos',
      label: 'Comisión Productos',
      content: <Card data-id="element-1266">
                <CardContent className="p-0 overflow-x-auto" data-id="element-1267">
                  <table className="w-full" data-id="element-1268">
                    <thead className="bg-gray-50 border-b" data-id="element-1269">
                      <tr data-id="element-1270">
                        <th className="px-4 py-3 text-left" data-id="element-1271">Fecha</th>
                        <th className="px-4 py-3 text-left" data-id="element-1272">Producto</th>
                        <th className="px-4 py-3 text-right" data-id="element-1273">Venta</th>
                        <th className="px-4 py-3 text-right" data-id="element-1274">Comisión</th>
                        <th className="px-4 py-3 text-center" data-id="element-1275">Usuario</th>
                      </tr>
                    </thead>

                    <tbody data-id="element-1276">
                      {productPagination.paginatedItems.map((row: any) => <tr key={row.id} className="border-b" data-id="element-1277">
                          <td className="px-4 py-3" data-id="element-1278">{row.date}</td>
                          <td className="px-4 py-3" data-id="element-1279">{row.product}</td>
                          <td className="px-4 py-3 text-right" data-id="element-1280">
                            {formatCurrency(row.total)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-600 font-bold" data-id="element-1281">
                            {formatCurrency(row.commission)}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-1282">
                            <Badge data-id="element-1283">{row.userName}</Badge>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>

                  <div className="p-4" data-id="element-1284">
                    <Pagination {...productPagination} data-id="element-1285" />
                  </div>
                </CardContent>
              </Card>
    }]} data-id="element-1241" />
    </div>;
}